import { graphql } from 'gatsby'
import React from "react"
import Page from '../components/layout/Page'
import SlicesMachine from '../prismic/SlicesMachine'
import Layout from '../components/layout/Layout'
import Helmet from '../components/abstract/Helmet'
import HelmetExport from 'react-helmet'
import { getHexFromDocument } from '../prismic/utility/colors'
import PropTypes from 'prop-types'

const CustomPageTemplate = ({ pageContext, ...props }) => {
  const { data } = props.data.prismicCustomPage

  return <Page context={pageContext}>
    {data.body.map((slice, index) => {
      if (slice.slice_type === "hero_banner" && slice.primary.background) {
        return <HelmetExport key={"helmet-" + index}>
          <meta name="theme-color" content={getHexFromDocument(slice.primary.background.document)}></meta>
        </HelmetExport>
      }
      return null
    }).filter(result => result !== null)}

    <Layout>
      <SlicesMachine slices={data.body} />
    </Layout>
  </Page>
}

CustomPageTemplate.propTypes = {
  data: PropTypes.shape({
    prismicCustomPage: PropTypes.shape({
      data: PropTypes.shape({
        body: PropTypes.array
      })
    })
  })
}

export default CustomPageTemplate

export const Head = ({ location, data, pageContext }) => {
  return <Helmet
    location={location}
    pageProps={data.prismicCustomPage.data}
    folder={pageContext.folder}
    alternatePages={pageContext.alternatePages}
    seoIndex={pageContext.seoIndex}
  />
}

export const pageQuery = graphql`
  query prismicCustomPage($pageId: String!) {
    prismicCustomPage(id: {eq: $pageId}) {
      id
      url
      lang
      type
      data {
        meta_title {
          text
        }
        exclude_fabrick
        meta_image {
          url
        }
        meta_description {
          text
        }
        breadcrumb {
          label {
            text
          }
          link {
            document {
              ... on PrismicArticle {
                url
              }
              ... on PrismicWhitepaper {
                url
              }
              ... on PrismicPodcast {
                url
              }
              ... on PrismicWebinar {
                url
              }
              ... on PrismicVideo {
                url
              }
              ... on PrismicCustomPage {
                url
              }
              ... on PrismicPressRelease {
                url
              }
              ... on PrismicPressReview {
                url
              }
              ... on PrismicJobPosition {
                url
              }
            }
            url
          }
        }
        body {
          ... on PrismicCustomPageDataBodySpace {
            id
            slice_label
            slice_type
            primary {
              mobile
              desktop
              custom_id {
                text
              }
            }
          }
          ... on PrismicCustomPageDataBodyText {
            id
            slice_label
            slice_type
            primary {
              negative
              border
              align_center
              text_size_override
              columns
              text {
                text
                html
              }
            }
          }
          ... on PrismicCustomPageDataBodyYoutubeVideo {
            id
            slice_label
            slice_type
            primary {
              width
              alignment
              video_code {
                text
              }
              rounded_border
            }
          }
          ... on PrismicCustomPageDataBodyImage {
            id
            slice_type
            slice_label
            primary {
              image_mobile {
                dimensions {
                  height
                  width
                }
                gatsbyImageData
                alt
              }
              image_desktop {
                dimensions {
                  height
                  width
                }
                gatsbyImageData
                alt
              }
              position
            }
          }
          ... on PrismicCustomPageDataBodyHeroBanner {
            id
            slice_label
            slice_type
            items {
              label {
                text
              }
              link {
                url
                target
                document {
                  ... on PrismicArticle {
                    url
                  }
                  ... on PrismicWhitepaper {
                    url
                  }
                  ... on PrismicPodcast {
                    url
                  }
                  ... on PrismicWebinar {
                    url
                  }
                  ... on PrismicVideo {
                    url
                  }
                  ... on PrismicCustomPage {
                    url
                  }
                  ... on PrismicPressRelease {
                    url
                  }
                  ... on PrismicPressReview {
                    url
                  }
                  ... on PrismicJobPosition {
                    url
                  }
                }
              }
              type
            }
            primary {
              type
              badge {
                text
              }
              badge_type
              negative
              background {
                document {
                  ... on PrismicColorsAndBackgrounds {
                    data {
                      hex_color
                      css_background {
                        text
                      }
                      css_background_image {
                        text
                      }
                      color {
                        document {
                          ... on PrismicColorsAndBackgrounds {
                            data {
                              hex_color
                            }
                          }
                        }
                      }
                      background_image {
                        url
                      }
                    }
                  }
                }
              }
              description {
                html
              }
              image {
                gatsbyImageData
              }
              title {
                text
                html
              }
              title_size
              bottom_padding
            }
          }
          ... on PrismicCustomPageDataBodyWrapperStart {
            id
            slice_label
            slice_type
            primary {
              rounded_corners
              background {
                document {
                  ... on PrismicColorsAndBackgrounds {
                    data {
                      hex_color
                      css_background {
                        text
                      }
                      css_background_image {
                        text
                      }
                      color {
                        document {
                          ... on PrismicColorsAndBackgrounds {
                            data {
                              hex_color
                            }
                          }
                        }
                      }
                      background_image {
                        url
                      }
                    }
                  }
                }
              }
            }
          }
          ... on PrismicCustomPageDataBodyWrapperEnd {
            id
            slice_label
            slice_type
          }
          ... on PrismicCustomPageDataBodyTab {
            id
            primary {
              label {
                text
              }
            }
            slice_type
            slice_label
          }
          ... on PrismicCustomPageDataBodyTabSectionEnd {
            id
            slice_label
            slice_type
          }
          ... on PrismicCustomPageDataBodyTabSectionStart {
            id
            slice_label
            slice_type
            primary {
              negative
            }
          }
          ... on PrismicCustomPageDataBodyButtonsGroup {
            id
            slice_label
            slice_type
            items {
              type
              label {
                text
              }
              link {
                url
                target
                document {
                  ... on PrismicArticle {
                    url
                  }
                  ... on PrismicPressRelease {
                    url
                  }
                  ... on PrismicPressReview {
                    url
                  }
                  ... on PrismicWhitepaper {
                    url
                  }
                  ... on PrismicPodcast {
                    url
                  }
                  ... on PrismicWebinar {
                    url
                  }
                  ... on PrismicVideo {
                    url
                  }
                  ... on PrismicCustomPage {
                    url
                  }
                  ... on PrismicPressRelease {
                    url
                  }
                  ... on PrismicPressReview {
                    url
                  }
                  ... on PrismicJobPosition {
                    url
                  }
                }
              }
            }
            primary {
              align_center
            }
          }
          ... on PrismicCustomPageDataBodyBadgesGroup {
            id
            slice_label
            slice_type
            items {
              label {
                text
              }
              link {
                url
                target
                document {
                  ... on PrismicArticle {
                    url
                  }
                  ... on PrismicPressRelease {
                    url
                  }
                  ... on PrismicPressReview {
                    url
                  }
                  ... on PrismicWhitepaper {
                    url
                  }
                  ... on PrismicPodcast {
                    url
                  }
                  ... on PrismicWebinar {
                    url
                  }
                  ... on PrismicVideo {
                    url
                  }
                  ... on PrismicCustomPage {
                    url
                  }
                  ... on PrismicPressRelease {
                    url
                  }
                  ... on PrismicPressReview {
                    url
                  }
                  ... on PrismicJobPosition {
                    url
                  }
                }
              }
              type
            }
            primary {
              align_center
            }
          }
          ... on PrismicCustomPageDataBodyForm {
            id
            slice_label
            slice_type
            primary {
              negative
              centered_box
              form
              hubspot_region {
                text
              }
              hubspot_form_id {
                text
              }
              hubspot_portal_id {
                text
              }
              form_name {
                text
              }
              button_label {
                text
              }
              button_link {
                url
                target
                document {
                  ... on PrismicArticle {
                    url
                  }
                  ... on PrismicPressRelease {
                    url
                  }
                  ... on PrismicPressReview {
                    url
                  }
                  ... on PrismicWhitepaper {
                    url
                  }
                  ... on PrismicPodcast {
                    url
                  }
                  ... on PrismicWebinar {
                    url
                  }
                  ... on PrismicVideo {
                    url
                  }
                  ... on PrismicCustomPage {
                    url
                  }
                  ... on PrismicPressRelease {
                    url
                  }
                  ... on PrismicPressReview {
                    url
                  }
                  ... on PrismicJobPosition {
                    url
                  }
                }
              }
              button_type
              text {
                html
                text
              }
              redirect {
                url
                target
                document {
                  ... on PrismicArticle {
                    url
                  }
                  ... on PrismicPressRelease {
                    url
                  }
                  ... on PrismicPressReview {
                    url
                  }
                  ... on PrismicWhitepaper {
                    url
                  }
                  ... on PrismicPodcast {
                    url
                  }
                  ... on PrismicWebinar {
                    url
                  }
                  ... on PrismicVideo {
                    url
                  }
                  ... on PrismicCustomPage {
                    url
                  }
                  ... on PrismicPressRelease {
                    url
                  }
                  ... on PrismicPressReview {
                    url
                  }
                  ... on PrismicJobPosition {
                    url
                  }
                }
              }
            }
            items {
              name {
                text
              }
              value {
                text
              }
            }
          }
          ... on PrismicCustomPageDataBodySimpleTable {
            id
            slice_type
            slice_label
            primary {
              special_table
              columns_layout {
                text
              }
              columns_number
              header_4 {
                html
                text
              }
              header_3 {
                html
                text
              }
              header_2 {
                html
                text
              }
              header_1 {
                html
                text
              }
            }
            items {
              column_4 {
                html
              }
              column_3 {
                html
              }
              column_2 {
                html
              }
              column_1 {
                html
              }
            }
          }
          ... on PrismicCustomPageDataBodyLogosGrid {
            id
            slice_type
            slice_label
            primary {
              columns_desktop
              columns_mobile
            }
            items {
              hide_on_mobile
              logo {
                gatsbyImageData
                alt
              }
            }
          }
          ... on PrismicCustomPageDataBodyTextImage {
            id
            slice_label
            slice_type
            items {
              tag_label {
                text
              }
            }
            primary {
              badge_link {
                url
                target
                document {
                  ... on PrismicArticle {
                    url
                  }
                  ... on PrismicPressRelease {
                    url
                  }
                  ... on PrismicPressReview {
                    url
                  }
                  ... on PrismicWhitepaper {
                    url
                  }
                  ... on PrismicPodcast {
                    url
                  }
                  ... on PrismicWebinar {
                    url
                  }
                  ... on PrismicVideo {
                    url
                  }
                  ... on PrismicCustomPage {
                    url
                  }
                  ... on PrismicPressRelease {
                    url
                  }
                  ... on PrismicPressReview {
                    url
                  }
                  ... on PrismicJobPosition {
                    url
                  }
                }
              }
              badge_text {
                text
              }
              badge_type
              button_label {
                text
              }
              button_link {
                url
                target
                document {
                  ... on PrismicArticle {
                    url
                  }
                  ... on PrismicPressRelease {
                    url
                  }
                  ... on PrismicPressReview {
                    url
                  }
                  ... on PrismicWhitepaper {
                    url
                  }
                  ... on PrismicPodcast {
                    url
                  }
                  ... on PrismicWebinar {
                    url
                  }
                  ... on PrismicVideo {
                    url
                  }
                  ... on PrismicCustomPage {
                    url
                  }
                  ... on PrismicPressRelease {
                    url
                  }
                  ... on PrismicPressReview {
                    url
                  }
                  ... on PrismicJobPosition {
                    url
                  }
                }
              }
              button_type
              desktop_image {
                dimensions {
                  height
                  width
                }
                gatsbyImageData
                alt
              }
              image_background {
                document {
                  ... on PrismicColorsAndBackgrounds {
                    data {
                      hex_color
                      css_background {
                        text
                      }
                      css_background_image {
                        text
                      }
                      color {
                        document {
                          ... on PrismicColorsAndBackgrounds {
                            data {
                              hex_color
                            }
                          }
                        }
                      }
                      background_image {
                        url
                      }
                    }
                  }
                }
              }
              image_position
              image_size
              mobile_image {
                dimensions {
                  height
                  width
                }
                gatsbyImageData
                alt
              }
              negative
              text {
                html
              }
              title {
                text
                html
              }
              title_size
            }
          }
          ... on PrismicCustomPageDataBodyRoundedCardsGrid {
            id
            slice_type
            slice_label
            primary_alias: primary {
              columns_desktop
              columns_tablet
              columns_mobile
              invert_order
              negative
            }
            items {
              hide_on_mobile
              hide_on_tablet
              image {
                gatsbyImageData(
                  placeholder: BLURRED
                )
                alt
              }
              icon {
                gatsbyImageData
                alt
              }
              tags {
                text
              }
              title {
                html
                text
              }
              description {
                html
              }
              button_style
              button_link {
                url
                target
                document {
                  ... on PrismicArticle {
                    url
                  }
                  ... on PrismicPressRelease {
                    url
                  }
                  ... on PrismicPressReview {
                    url
                  }
                  ... on PrismicWhitepaper {
                    url
                  }
                  ... on PrismicPodcast {
                    url
                  }
                  ... on PrismicWebinar {
                    url
                  }
                  ... on PrismicVideo {
                    url
                  }
                  ... on PrismicCustomPage {
                    url
                  }
                  ... on PrismicPressRelease {
                    url
                  }
                  ... on PrismicPressReview {
                    url
                  }
                  ... on PrismicJobPosition {
                    url
                  }
                }
              }
              background {
                document {
                  ... on PrismicColorsAndBackgrounds {
                    data {
                      hex_color
                      css_background {
                        text
                      }
                      css_background_image {
                        text
                      }
                      color {
                        document {
                          ... on PrismicColorsAndBackgrounds {
                            data {
                              hex_color
                            }
                          }
                        }
                      }
                      background_image {
                        url
                      }
                    }
                  }
                }
              }
              button_text {
                text
              }
            }
          }
          ... on PrismicCustomPageDataBodyRoundedTextColumns {
            id
            slice_label
            slice_type
            primary {
              negative
              background_color {
                document {
                  ... on PrismicColorsAndBackgrounds {
                    data {
                      hex_color
                    }
                  }
                }
              }
            }
            items {
              title {
                text
              }
              large_number {
                text
              }
              description {
                html
              }
            }
          }
          ... on PrismicCustomPageDataBodyQuote {
            id
            slice_label
            slice_type
            primary {
              description {
                html
                text
              }
              negative
              quote_size
              quote {
                text
              }
              photo {
                gatsbyImageData
                alt
              }
              image_right {
                gatsbyImageData
                alt
              }
            }
          }
          ... on PrismicCustomPageDataBodyCustomTypesList {
            slice_label
            slice_type
            id
            primary {
              custom_type
              title {
                text
              }
              visibles
            }
            items {
              ignored_documents {
                document {
                  ... on PrismicArticle {
                    id
                  }
                  ... on PrismicVideo {
                    id
                  }
                  ... on PrismicWhitepaper {
                    id
                  }
                  ... on PrismicPodcast {
                    id
                  }
                  ... on PrismicWebinar {
                    id
                  }
                }
              }
            }
          }
          ... on PrismicCustomPageDataBodyTextBlocksRightImage {
            id
            slice_type
            slice_label
            primary {
              negative
              title {
                text
              }
              description {
                html
                text
              }
              right_image_mobile {
                gatsbyImageData
                alt
              }
              right_image_desktop {
                alt
                gatsbyImageData
              }
              icon_columns
              image_background_color {
                document {
                  ... on PrismicColorsAndBackgrounds {
                    data {
                      hex_color
                    }
                  }
                }
              }
            }
            items {
              title {
                text
              }
              icon {
                gatsbyImageData
                alt
                localFile {
                  extension
                }
              }
              description {
                html
                text
              }
              button_text {
                text
              }
              button_link {
                url
                target
                document {
                  ... on PrismicArticle {
                    url
                  }
                  ... on PrismicPressRelease {
                    url
                  }
                  ... on PrismicPressReview {
                    url
                  }
                  ... on PrismicWhitepaper {
                    url
                  }
                  ... on PrismicPodcast {
                    url
                  }
                  ... on PrismicWebinar {
                    url
                  }
                  ... on PrismicVideo {
                    url
                  }
                  ... on PrismicCustomPage {
                    url
                  }
                  ... on PrismicPressRelease {
                    url
                  }
                  ... on PrismicPressReview {
                    url
                  }
                  ... on PrismicJobPosition {
                    url
                  }
                }
              }
            }
          }
          ... on PrismicCustomPageDataBodyMarkdown {
            primary {
              negative
              columns_desktop
              markdown {
                richText
                text
                html
              }
            }
            id
            slice_label
            slice_type
          }
          ... on PrismicCustomPageDataBodyRoundedCardsBox {
            id
            slice_type
            slice_label
            primary {
              negative
              title {
                text
                html
              }
              icon {
                gatsbyImageData
                alt
              }
              background_color {
                document {
                  ... on PrismicColorsAndBackgrounds {
                    data {
                      hex_color
                    }
                  }
                }
              }
            }
            items {
              title {
                html
                text
              }
              icon {
                gatsbyImageData
              }
              description {
                html
                text
              }
              button_text {
                text
              }
              button_link {
                url
                target
                document {
                  ... on PrismicArticle {
                    url
                  }
                  ... on PrismicPressRelease {
                    url
                  }
                  ... on PrismicPressReview {
                    url
                  }
                  ... on PrismicWhitepaper {
                    url
                  }
                  ... on PrismicPodcast {
                    url
                  }
                  ... on PrismicWebinar {
                    url
                  }
                  ... on PrismicVideo {
                    url
                  }
                  ... on PrismicCustomPage {
                    url
                  }
                  ... on PrismicPressRelease {
                    url
                  }
                  ... on PrismicPressReview {
                    url
                  }
                  ... on PrismicJobPosition {
                    url
                  }
                }
              }
            }
          }
          ... on PrismicCustomPageDataBodySpacialSections {
            id
            slice_label
            slice_type
            primary {
              negative
              section
            }
          }
          ... on PrismicCustomPageDataBodyCardsWithImage {
            id
            slice_label
            slice_type
            items {
              card_title {
                html
                text
              }
              image {
                gatsbyImageData
                alt
              }
              link {
                target
                document{
                  ... on PrismicArticle {
                    url
                  }
                  ... on PrismicPressRelease {
                    url
                  }
                  ... on PrismicPressReview {
                    url
                  }
                  ... on PrismicWhitepaper {
                    url
                  }
                  ... on PrismicPodcast {
                    url
                  }
                  ... on PrismicWebinar {
                    url
                  }
                  ... on PrismicVideo {
                    url
                  }
                  ... on PrismicCustomPage {
                    url
                  }
                  ... on PrismicPressRelease {
                    url
                  }
                  ... on PrismicPressReview {
                    url
                  }
                  ... on PrismicJobPosition {
                    url
                  }
                }
                url
              }
              link_label {
                text
              }
              text {
                html
                text
              }
            }
            primary {
              card_size
              negative
              cards_position
            }
          }
          ... on PrismicCustomPageDataBodyRoundedCardsScrollable {
            id
            slice_label
            slice_type
            primary {
              negative
              right_to_left
            }
            items {
              image {
                gatsbyImageData
                dimensions {
                  height
                  width
                }
              }
              text {
                html
                text
              }
              title {
                html
                text
              }
            }
          }
          ... on PrismicCustomPageDataBodyEventsCalendar {
            id
            slice_label
            slice_type
            primary {
              events_to_show
            }
          }
          ... on PrismicCustomPageDataBodyCardWithHorizontalContent {
            id
            slice_label
            slice_type
            primary {
              negative
            }
            items {
              button_1_link {
                document{
                  ... on PrismicArticle {
                    url
                  }
                  ... on PrismicPressRelease {
                    url
                  }
                  ... on PrismicPressReview {
                    url
                  }
                  ... on PrismicWhitepaper {
                    url
                  }
                  ... on PrismicPodcast {
                    url
                  }
                  ... on PrismicWebinar {
                    url
                  }
                  ... on PrismicVideo {
                    url
                  }
                  ... on PrismicCustomPage {
                    url
                  }
                  ... on PrismicPressRelease {
                    url
                  }
                  ... on PrismicPressReview {
                    url
                  }
                  ... on PrismicJobPosition {
                    url
                  }
                }
                url
              }
              button_1_style
              button_1_text {
                text
              }
              button_2_link {
                document{
                  ... on PrismicArticle {
                    url
                  }
                  ... on PrismicPressRelease {
                    url
                  }
                  ... on PrismicPressReview {
                    url
                  }
                  ... on PrismicWhitepaper {
                    url
                  }
                  ... on PrismicPodcast {
                    url
                  }
                  ... on PrismicWebinar {
                    url
                  }
                  ... on PrismicVideo {
                    url
                  }
                  ... on PrismicCustomPage {
                    url
                  }
                  ... on PrismicPressRelease {
                    url
                  }
                  ... on PrismicPressReview {
                    url
                  }
                  ... on PrismicJobPosition {
                    url
                  }
                }
                url
              }
              button_2_style
              button_2_text {
                text
              }
              content {
                html
                text
              }
              image {
                url
                gatsbyImageData
              }
              title {
                text
              }
            }
          }
          ... on PrismicCustomPageDataBodyTextButtonColoredBackgroundBox {
            id
            slice_label
            slice_type
            primary {
              background_color {
                document {
                  ... on PrismicColorsAndBackgrounds {
                    data {
                      hex_color
                      css_background_image {
                        text
                      }
                      css_background {
                        text
                      }
                      background_image {
                        url
                      }
                      color {
                        document {
                          ... on PrismicColorsAndBackgrounds {
                            data {
                              hex_color
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
              negative
              title_size
              title {
                html
                text
              }
              description {
                html
                text
              }
            }
            items {
              button_text {
                text
              }
              button_link {
                url
                target
                document {
                  ... on PrismicWhitepaper {
                    url
                  }
                  ... on PrismicWebinar {
                    url
                  }
                  ... on PrismicVideo {
                    url
                  }
                  ... on PrismicTe {
                    url
                  }
                  ... on PrismicPressReview {
                    url
                  }
                  ... on PrismicPressRelease {
                    url
                  }
                  ... on PrismicPodcast {
                    url
                  }
                  ... on PrismicPaymentMethod {
                    url
                  }
                  ... on PrismicJobPosition {
                    url
                  }
                  ... on PrismicInsightCategory {
                    url
                  }
                  ... on PrismicEvent {
                    url
                  }
                  ... on PrismicCustomPage {
                    url
                  }
                  ... on PrismicBannerAd {
                    url
                  }
                  ... on PrismicBank {
                    url
                  }
                  ... on PrismicAlternativePayment {
                    url
                  }
                  ... on PrismicArticle {
                    url
                  }
                  ... on PrismicEv {
                    url
                  }
                }
              }
            }
          }
        }
      }
      uid
    }
  }
`
